// ProductDetail.js - Updated to use cart store with variation price management
import React, { useEffect, useState } from "react";
import classNames from "classnames/bind";
import { ChevronsDown, Heart, LoaderCircle, MapPin } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./ProductDetail.module.scss";

import ProductSlideshow from "../../components/ProductSlideshow/ProductSlideshow";
import {
  getProductDetail,
  getProductsByCategories,
} from "../../services/Products/ProductServices";
import ImageGallery from "../../components/ImageGallery/ImageGallery";
import { useLanguageStore } from "../../stores/Language/useLanguageStore";
import useCartStore from "../../stores/Cart/useCartStore"; // Import cart store
import useCheckoutProductStore from "../../stores/CheckoutProduct/useCheckoutProductStore"; // Import checkout store
import useWishlistStore from "../../stores/WishList/WishListStore";

import { transformProductRecomend } from "../../utils/transformProductRecomend";

// Import variation management hook for price updates
import { useProductVariations } from "../../hooks/useProductVariations";
import useTranslations from "../../hooks/useTranslations";
import { translateProductName } from "../../utils/translateProductName";

const cx = classNames.bind(styles);

const ProductDetail = () => {
  const { id, slugName } = useParams();
  const navigate = useNavigate(); // Add useNavigate hook
  const { formatPrice, currentLanguage } = useLanguageStore(); // Chỉ lấy formatPrice từ store
  const currentTranslations = useTranslations(); // Sử dụng hook cho translations
  const { isInWishlist, toggleWishlist } = useWishlistStore();
  const [errorMessage, setErrorMessage] = useState("");

  // Cart store
  const { addToCart, isAddingToCart, clearError, isInCart } = useCartStore();

  // Checkout store for buy now functionality
  const { addCheckoutItem, clearCheckoutItems } = useCheckoutProductStore();

  // Variation management hook for price updates
  const {
    selectedVariation,
    currentPriceInfo,
    isLoadingVariations,
    selectVariationByOption,
  } = useProductVariations(id);

  // Local state
  const [product, setProduct] = useState({});
  const [productRecomend, setProductRecomend] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isFavorite, setIsFavorite] = useState(false);
  const [isAddBag, setIsAddBag] = useState(false);
  const [selected, setSelected] = useState("");
  const [imagesProduct, setImagesProduct] = useState([]);
  const [selectedVariationId, setSelectedVariationId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    details: false,
    tips: false,
    availability: false,
  });

  // Check if item is in cart
  const categorySlug = product?.categories?.[0]?.slug;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getCurrentDisplayPrice = () => {
    if (currentPriceInfo) {
      return currentPriceInfo.onSale
        ? Number(currentPriceInfo.salePrice || 0)
        : Number(currentPriceInfo.price || 0);
    }

    if (product.sale_price && product.sale_price < product.regular_price) {
      return Number(product.sale_price || 0);
    }

    return Number(product.price || product.regular_price || 0);
  };

  const getProductDisplayInfo = () => ({
    id: product.id,
    product_id: product.id,
    name: product.name || `Product ${product.id}`,
    slug: product.slug || slugName || "",
    price: getCurrentDisplayPrice(),
    regular_price: Number(product.regular_price || product.price || 0),
    sale_price: Number(product.sale_price || 0),
    image: product.images?.[0]?.src || "",
    selected,
    category: product?.categories?.[0]?.slug || "",
  });

  const handleFavoriteClick = () => {
    toggleWishlist(product.id, getProductDisplayInfo());
    setIsFavorite(!isFavorite);
  };

  const handleAddToBag = async () => {
    if (!product.id || isAddingToCart) return;

    if (!selected) {
      setErrorMessage("Vui lòng chọn option");
      return;
    }
    clearError();

    const productIdToAdd = parseInt(id);

    // Use variation from hook if available, otherwise use selectedVariationId
    const variationId = selectedVariationId || selectedVariation?.id;
    let attributesData = null;

    if (variationId && selected) {
      if (selectedVariation) {
        // Use variation hook data if available
        attributesData = {};
        attributesData = Object.fromEntries(
          selectedVariation.attributes?.map((attr) => [
            `attribute_${attr.slug.toLowerCase()}`,
            attr.option,
          ]) || []
        );
      } else {
        // Fallback to original method
        const sizeAttribute = product.attributes?.find(
          (attr) => attr.name === "Size" || attr.name === "Option"
        );
        if (sizeAttribute) {
          attributesData = {
            [`attribute_${sizeAttribute.slug.toLowerCase()}`]: selected,
          };
        }
      }
    }

    const result = await addToCart(
      productIdToAdd,
      1,
      variationId,
      attributesData,
      getProductDisplayInfo()
    );

    if (result.success) {
      setIsAddBag(true);
      setTimeout(() => {
        setIsAddBag(false);
      }, 2000);
    } else {
      console.error("Failed to add to cart:", result.error);
    }
  };

  const handleBuyNow = async () => {
    if (!product.id || isAddingToCart) return;
    if (!selected) {
      setErrorMessage("Vui lòng chọn option");
      return;
    }
    clearError();

    const productIdToAdd = parseInt(id);

    // Use variation from hook if available, otherwise use selectedVariationId
    const variationId = selectedVariationId || selectedVariation?.id;
    let attributesData = null;

    if (variationId && selected) {
      if (selectedVariation) {
        // Use variation hook data if available
        attributesData = {};
        attributesData = Object.fromEntries(
          selectedVariation.attributes?.map((attr) => [
            `attribute_${attr.slug.toLowerCase()}`,
            attr.option,
          ]) || []
        );
      } else {
        // Fallback to original method
        const sizeAttribute = product.attributes?.find(
          (attr) => attr.name === "Size" || attr.name === "Option"
        );
        if (sizeAttribute) {
          attributesData = {
            [`attribute_${sizeAttribute.slug.toLowerCase()}`]: selected,
          };
        }
      }
    }

    // Create checkout item from current product and options
    const checkoutItem = {
      id: productIdToAdd,
      name: product.name,
      price: currentPriceInfo
        ? currentPriceInfo.onSale
          ? currentPriceInfo.salePrice
          : currentPriceInfo.price
        : product.sale_price && product.sale_price < product.regular_price
        ? parseFloat(product.sale_price)
        : parseFloat(product.price),
      quantity: 1,
      image: product.images?.[0]?.src || "",
      selected: selected,
      variation_id: variationId,
      attributes: attributesData,
      cart_item_key: `${productIdToAdd}_${
        variationId || "no_variation"
      }_${Date.now()}`, // Generate unique key
      category: product?.categories?.[0].slug,
      checkout_source: "buy_now",
    };

    // Clear any existing checkout items and add this one
    clearCheckoutItems();
    addCheckoutItem(checkoutItem);

    // Navigate to checkout page
    navigate("/checkout");
  };

  const handleSelect = (name) => {
    setSelected(name);
    setErrorMessage("");
    // Find the index of the selected option in the Size attribute
    let attr = product.attributes.find(
      (attr) => attr.slug === "Size" || attr.slug === "Option"
    );
    let attributeName = attr.slug;

    // Use variation hook to update price
    selectVariationByOption(name, attributeName);

    // Find the correct variation ID based on the selected option name
    // This will be set when selectedVariation changes in the useEffect below
  };

  const transformedImageProducts = (images) => {
    const listImage = images.map((image) => {
      return {
        original: image.src,
        thumbnail: image.src,
      };
    });

    return listImage;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      let data = await getProductDetail(id);
      if (data) {
        setProduct(data);

        // Set initial selected option to the first available option from "Option" or "Size" attribute
        if (
          data.variations.length > 0 &&
          data.attributes &&
          data.attributes.length > 0
        ) {
          // Determine attribute name based on category
          const categorySlug = data?.categories?.[0]?.slug;
          const attribute = data?.attributes?.find(
            (attr) => attr.slug === "Size" || attr.slug === "Option"
          );
          // || categorySlug?.toLowerCase() === "rings" ? "Size" : "Option"
          const attributeName = attribute?.slug;

          const optionAttribute = data.attributes.find(
            (attr) =>
              attr.name === attributeName &&
              attr.options &&
              attr.options.length > 0
          );

          if (optionAttribute && optionAttribute.options.length > 0) {
            const firstOption = optionAttribute.options[0];

            // Set selected option
            setSelected(firstOption);

            // Use variation hook - this will handle pending selection if variations not loaded yet
            selectVariationByOption(firstOption, attributeName);
          }
        } else if (data.variations.length < 1) {
          const attribute = data?.attributes?.find(
            (attr) => attr.slug === "Size" || attr.slug === "Option"
          )?.options?.[0];

          setSelected(attribute);
        }
        // data.images
        setImagesProduct(
          data.images.length > 0 && transformedImageProducts(data.images)
        );
      }
      setIsLoading(false);
    };

    fetchData();
  }, [id, slugName]);

  // Sync selectedVariationId with selectedVariation from hook
  useEffect(() => {
    if (selectedVariation?.id) {
      setSelectedVariationId(selectedVariation.id);
    }
  }, [selectedVariation]);

  // Clear cart error when component unmounts
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  useEffect(() => {
    const fetchProductRecomend = async () => {
      if (!categorySlug) return;

      const res = await getProductsByCategories(categorySlug);
      if (res?.products) {
        const transformed = res.products.map(transformProductRecomend);
        setProductRecomend(transformed);
      }
    };
    fetchProductRecomend();
  }, [categorySlug]);

  const cleanDescription = (html) => {
    if (!html) return;

    return html.replace(/<[^>]*>/g, "");
  };

  // Helper function to display price with sale info
  const displayPrice = () => {
    if (currentPriceInfo) {
      if (currentPriceInfo.onSale) {
        return (
          <div className={cx("price-with-sale")}>
            <span className={cx("sale-price")}>
              {formatPrice(currentPriceInfo.salePrice)}
            </span>
            <span className={cx("regular-price")}>
              {formatPrice(currentPriceInfo.regularPrice)}
            </span>
          </div>
        );
      } else {
        return (
          <span className={cx("price")}>
            {formatPrice(currentPriceInfo.price)}
          </span>
        );
      }
    }

    if (product && product.sale_price) {
      return (
        <div className={cx("price-with-sale")}>
          <span className={cx("sale-price")}>
            {formatPrice(product.sale_price)}
          </span>
          <span className={cx("regular-price")}>
            {formatPrice(product.regular_price)}
          </span>
        </div>
      );
    }

    return <span className={cx("price")}>{formatPrice(product.price)}</span>;
  };

  return (
    <div className={cx("container__product")}>
      {isLoading ? (
        <div className={cx("container_loading")}>
          <LoaderCircle size={48} className={cx("loader")} />
        </div>
      ) : (
        <div className={cx("product-main")}>
          <div className={cx("product-image")}>
            {imagesProduct.length > 0 && (
              <ImageGallery images={imagesProduct} />
            )}
          </div>
          <div className={cx("product-content")}>
            <div className={cx("product-header")}>
              <div className={cx("product-title-price")}>
                <div className={cx("product-title-row")}>
                  <h1 className={cx("title")}>
                    {translateProductName(product.name)}
                  </h1>
                  <button
                    className={cx("favorite-btn", {
                      active: isInWishlist(product.id),
                    })}
                    onClick={handleFavoriteClick}
                  >
                    <Heart />
                  </button>
                </div>
                {displayPrice()}
              </div>
            </div>

            <div className={cx("product-description")}>
              <p>
                {currentLanguage === "vi"
                  ? cleanDescription(product.description)
                  : cleanDescription(product.short_description)}
              </p>

              <div className={cx("product-option")}>
                {product.attributes &&
                  product.attributes
                    .filter(
                      (attribute) =>
                        attribute.name === "Option" || attribute.name === "Size"
                    )
                    .map((attribute) => (
                      <div
                        key={attribute.id}
                        className={cx("product-option-section")}
                      >
                        <h3 className={cx("product-option-title")}>
                          {attribute.name}
                        </h3>
                        <div className={cx("product-option-list")}>
                          {attribute.options.map((item, index) => (
                            <div
                              key={`${item}-${index}`}
                              className={cx("product-option-item")}
                              onClick={() => handleSelect(item)}
                            >
                              <div
                                className={cx("product-option-box", {
                                  selected: selected === item,
                                })}
                              >
                                <span className={cx("option-text")}>
                                  {item}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
              </div>
              <div className={cx("product-error")}>
                <p>{errorMessage}</p>
              </div>
              <div className={cx("product-action")}>
                <button
                  className={cx("btn", "add-btn", {
                    added: isAddBag || isAddingToCart,
                    loading: isAddingToCart || isLoadingVariations,
                  })}
                  onClick={handleAddToBag}
                  disabled={isAddBag || isAddingToCart || isLoadingVariations}
                >
                  {isLoadingVariations
                    ? "LOADING..."
                    : isAddingToCart
                    ? "ADDING..."
                    : isAddBag
                    ? currentTranslations?.productDetail?.added
                    : currentTranslations?.productDetail?.add_to_bag}
                </button>
                <button
                  className={cx("btn", "buy-btn")}
                  onClick={handleBuyNow}
                  disabled={isAddBag || isAddingToCart || isLoadingVariations}
                >
                  {currentTranslations?.productDetail?.buy_now}
                </button>
              </div>
            </div>

            <div className={cx("product-details")}>
              {/* Details Section */}
              <div className={cx("detail-section")}>
                <div
                  className={cx("section-header")}
                  onClick={() => toggleSection("details")}
                >
                  <span>{currentTranslations?.productDetail?.details}</span>
                  <ChevronsDown
                    className={cx("chevron-icon", {
                      rotated: expandedSections.details,
                    })}
                  />
                </div>
                <div
                  className={cx("section-content", {
                    expanded: expandedSections.details,
                  })}
                >
                  <ul className={cx("content-list")}>
                    {product.attributes &&
                      product.attributes
                        .filter(
                          (attr) =>
                            attr.name === "Type" || attr.name === "Gemstone"
                        )
                        .flatMap((attr) =>
                          attr.options.map((option, index) => (
                            <li
                              className={cx("content-item")}
                              key={`${attr.name}-${index}`}
                            >
                              {option}
                            </li>
                          ))
                        )}
                  </ul>
                </div>
              </div>
              {/* Tips & Warnings Section - Custom for rings/nhẫn */}
              {categorySlug?.toLowerCase() === "rings" ||
              categorySlug?.toLowerCase() === "nhan" ? (
                <>
                  {/* Tips: Steps to measure ring size */}
                  <div className={cx("detail-section")}>
                    <div
                      className={cx("section-header")}
                      onClick={() => toggleSection("tips")}
                    >
                      <span>
                        {currentTranslations?.productDetail?.tips}{" "}
                        {/* or custom: "Các bước đo size nhẫn" */}
                      </span>
                      <ChevronsDown
                        className={cx("chevron-icon", {
                          rotated: expandedSections.tips,
                        })}
                      />
                    </div>
                    <div
                      className={cx("section-content", {
                        expanded: expandedSections.tips,
                      })}
                    >
                      <ul className={cx("content-list")}>
                        <li className={cx("content-item")}>
                          {currentTranslations?.RingSizeGuide?.Step1}
                        </li>
                        <li className={cx("content-item")}>
                          {currentTranslations?.RingSizeGuide?.Step2}
                        </li>
                        <li className={cx("content-item")}>
                          {currentTranslations?.RingSizeGuide?.Step3}
                        </li>
                        <li className={cx("content-item")}>
                          {currentTranslations?.RingSizeGuide?.Step4}
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* Warnings: Jewelry care tips */}
                  <div className={cx("detail-section")}>
                    <div
                      className={cx("section-header")}
                      onClick={() => toggleSection("warnings")}
                    >
                      <span>
                        {currentTranslations?.productDetail?.warnings ||
                          "Cảnh báo & Bảo quản"}
                      </span>
                      <ChevronsDown
                        className={cx("chevron-icon", {
                          rotated: expandedSections.warnings,
                        })}
                      />
                    </div>
                    <div
                      className={cx("section-content", {
                        expanded: expandedSections.warnings,
                      })}
                    >
                      <ul className={cx("content-list")}>
                        <li className={cx("content-item")}>
                          {currentTranslations?.JewelryCare?.Line1}
                        </li>
                        <li className={cx("content-item")}>
                          {currentTranslations?.JewelryCare?.Line2}
                        </li>
                        <li className={cx("content-item")}>
                          {currentTranslations?.JewelryCare?.Line3}
                        </li>
                      </ul>
                    </div>
                  </div>
                </>
              ) : (
                <div className={cx("detail-section")}>
                  <div
                    className={cx("section-header")}
                    onClick={() => toggleSection("tips")}
                  >
                    <span>
                      {currentTranslations?.productDetail?.tips_warnings}
                    </span>
                    <ChevronsDown
                      className={cx("chevron-icon", {
                        rotated: expandedSections.tips,
                      })}
                    />
                  </div>
                  <div
                    className={cx("section-content", {
                      expanded: expandedSections.tips,
                    })}
                  >
                    <ul className={cx("content-list")}>
                      <li className={cx("content-item")}>
                        {currentTranslations?.JewelryCare?.Line1}
                      </li>
                      <li className={cx("content-item")}>
                        {currentTranslations?.JewelryCare?.Line2}{" "}
                      </li>
                      <li className={cx("content-item")}>
                        {currentTranslations?.JewelryCare?.Line3}{" "}
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Using the new ProductSlideshow component */}
      <ProductSlideshow
        column={true}
        showBtn={false}
        items={productRecomend}
        itemsPerPage={4}
        title={currentTranslations?.productDetail?.recommended_for_you}
        subtitle={currentTranslations?.productDetail?.based_on_your_history}
      />
    </div>
  );
};

export default ProductDetail;
